import React, {useCallback, Suspense, useState, useEffect, lazy} from 'react';
import PropTypes from 'prop-types';
import {getCampaignLink} from '~/utils/url';
import {useDispatch, useSelector} from 'react-redux';
import {matchPath, useLocation, useNavigate, useRoutes} from 'react-router-dom';
import {notFoundRoute} from '~/router/routerHelpers';
import RouteHandlerConnector from './RouteHandlerConnector';
import MediaQueries, {breakpoint} from '~/constants/MediaQueries';
import {resetAfterSavePath} from '~/slices/pathSlice';
import {isBlank} from '~/selectors/CanvasSelectors';

import './NextToolsRoutes.scss';

const CampaignCard = lazy(
  () => import('~/components/CampaignCard'),
  'CampaignCard'
);
const CampaignGoalsCard = lazy(
  () => import('~/components/CampaignGoalsCard'),
  'CampaignGoalsCard'
);
const ContentCheckCard = lazy(
  () => import('~/components/DesignChecker/ContentCheckCard'),
  'ContentCheckCard'
);
const SaveConfirmation = lazy(
  () => import('~/components/SaveConfirmation'),
  'SaveConfirmation'
);
const EmailConfirmation = lazy(
  () => import('~/components/EmailConfirmation'),
  'EmailConfirmation'
);
const MultiItemSummaryView = lazy(
  () => import('~/components/Quote/MultiItemSummaryView'),
  'MultiItemSummaryView'
);
const DesignChecker = lazy(
  () => import('~/components/DesignChecker'),
  'DesignChecker'
);
const DesignSpinner = lazy(
  () => import('~/components/DesignSpinner'),
  'DesignSpinner'
);
const IconSwatchColorChanger = lazy(
  () => import('~/components/IconSwatchColorChanger'),
  'IconSwatchColorChanger'
);
const MoneySavingTipsView = lazy(
  () => import('~/components/Quote/MoneySavingTipsView'),
  'MoneySavingTipsView'
);
const ShareEmailForm = lazy(
  () => import('~/components/ShareEmailForm'),
  'ShareEmailForm'
);
const SaveForm = lazy(() => import('~/components/SaveForm'), 'SaveForm');
import NextMenu from '~/components/NextMenu';
const Quote = lazy(() => import('~/components/Quote'), 'Quote');
const InventoryIntro = lazy(
  () => import('~/components/InventoryIntro'),
  'InventoryIntro'
);
const InternationalQuote = lazy(
  () => import('~/components/Quote/InternationalQuote'),
  'InternationalQuote'
);
const ConfirmInternational = lazy(
  () => import('~/components/ConfirmInternational'),
  'ConfirmInternational'
);
const AddedToCart = lazy(
  () => import('~/components/AddedToCart'),
  'AddedToCart'
);
const GofOverview = lazy(
  () => import('~/components/GofOverview'),
  'GofOverview'
);
const GofQuote = lazy(() => import('~/components/GofQuote'), 'GofQuote');
const GofQuantity = lazy(
  () => import('~/components/GofQuote/GofQuantity'),
  'GofQuantity'
);
import OrderType from '~/components/OrderType';
import ZipQuantity from '~/components/ZipQuantity';
const Price = lazy(() => import('~/components/Price'), 'Price');
const ImpossibleQuote = lazy(
  () => import('~/components/ImpossibleQuote'),
  'ImpossibleQuote'
);

// Eager loaded components
import ContentCard from '~/components/ContentCard';
import Panel from '~/components/Panel';
import {simplePricingTestEnabled} from '~/selectors/ConfigSelectors';

const propTypes = {
  designDirty: PropTypes.bool,
  hooks: PropTypes.object,
  setAfterSavePath: PropTypes.func,
  setCombineDesigns: PropTypes.func,
  setOrderOptionsPath: PropTypes.func
};

/** @param {PropTypes.InferProps<typeof propTypes>} props - Component properties. */
const NextToolsRoutes = props => {
  const {
    designDirty,
    hooks,
    setAfterSavePath,
    setCombineDesigns,
    setOrderOptionsPath
  } = props;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const blankDesign = useSelector(isBlank);
  const simplePricingEnabled = useSelector(simplePricingTestEnabled);
  const location = useLocation();
  const [displayLocation, setDisplayLocation] = useState(location);
  const [transitionStage, setTransistionStage] = useState('slideIn');
  const isMobile = breakpoint(MediaQueries.mediumAndBelow);
  const confirmContinue = /\?continue=true/.test(window.location.href);

  useEffect(() => {
    if (!matchPath('/next/*', displayLocation?.pathname) && !isMobile) {
      setTransistionStage('slideOut');
    } else if (location?.pathname !== displayLocation?.pathname) {
      setDisplayLocation(location);
    }
  }, [location, displayLocation, isMobile]);

  const onClose = useCallback(() => navigate('/'), [navigate]);

  const handleQuoteClick = useCallback(() => {
    setCombineDesigns();
    getCampaignLink()
      ? (window.location.href = getCampaignLink())
      : navigate('/next/quote');
  }, [setCombineDesigns, navigate]);

  const onCampaignClick = useCallback(() => {
    setOrderOptionsPath('/next/fundraisingGoals');
    navigate('/next/fundraisingGoals');
  }, [navigate, setOrderOptionsPath]);

  const handleGofSaveDesign = useCallback(() => {
    navigate('/next/saveForm');
  }, [navigate]);

  const handleGofSetUp = useCallback(() => {
    if (designDirty) {
      setAfterSavePath('/gof');
      navigate('/next/saveForm');
    } else {
      navigate('/gof');
    }
  }, [designDirty, setAfterSavePath, navigate]);

  const render = useCallback(
    component => (
      <Suspense fallback={<ContentCard />}>
        <Panel>{component}</Panel>
      </Suspense>
    ),
    []
  );

  const getNextRoutesIndex = () => {
    if (simplePricingEnabled) {
      return <ZipQuantity onClose={onClose} />;
    } else {
      return <OrderType onClose={onClose} />;
    }
  };

  const nextRoutesIndex = {
    index: true,
    element: render(getNextRoutesIndex())
  };

  const orderType = simplePricingEnabled
    ? {
        element: <OrderType onClose={onClose} />,
        path: 'order-type'
      }
    : {};

  const routes = useRoutes(
    [
      {
        path: 'next',
        children: [
          {...nextRoutesIndex},
          {...orderType},
          {
            element: render(<ImpossibleQuote />),
            path: 'unquoteable'
          },
          {
            element: render(
              <NextMenu
                onClose={() => {
                  onClose();
                  dispatch(resetAfterSavePath());
                }}
                onQuoteClick={handleQuoteClick}
              />
            ),
            path: 'order-options'
          },
          {
            element: render(
              <NextMenu
                onClose={() => {
                  onClose();
                  dispatch(resetAfterSavePath());
                }}
                onQuoteClick={handleQuoteClick}
              />
            ),
            path: 'fromQuote'
          },
          {
            element: render(<EmailConfirmation />),
            path: 'emailConfirmation'
          },
          {
            element: render(
              <SaveConfirmation confirmContinue={confirmContinue} rightPanel />
            ),
            path: 'saveConfirmation'
          },
          {
            element: render(
              <SaveConfirmation
                confirmContinue={confirmContinue}
                rightPanel
                gof
              />
            ),
            path: 'gof'
          },
          {
            element: render(<AddedToCart rightPanel />),
            path: 'addedToCart'
          },
          {
            element: render(<SaveForm rightPanel />),
            path: 'saveForm'
          },
          {
            element: render(<SaveForm continue rightPanel />),
            path: 'saveContinue'
          },
          {
            path: 'verify',
            children: [
              {
                element: render(<SaveForm rightPanel verify />),
                index: true
              },
              {
                element: render(<SaveForm rightPanel verify />),
                path: 'quote'
              }
            ]
          },
          {
            element: render(<SaveForm rightPanel verify checkout />),
            path: 'checkout'
          },
          {
            element: render(
              <SaveForm isInternational verify rightPanel verifyIntl />
            ),
            path: 'verifyIntl'
          },
          {
            element: render(<ShareEmailForm onClose={onClose} />),
            path: 'shareEmail'
          },
          {
            element: render(
              <CampaignCard
                blankDesign={blankDesign}
                onCampaignClick={onCampaignClick}
                onClose={onClose}
              />
            ),
            path: 'fundraising'
          },
          {
            element: render(<CampaignGoalsCard onClose={onClose} />),
            path: 'fundraisingGoals'
          },
          {
            element: render(<ConfirmInternational />),
            path: 'confirmIntl'
          },
          {
            element: render(
              <GofOverview
                blankDesign={blankDesign}
                designDirty={props.designDirty}
                setAfterSavePath={props.setAfterSavePath}
                onClose={onClose}
              />
            ),
            path: 'gofOverview'
          },
          {
            element: render(<GofQuantity />),
            path: 'gofQty'
          },
          {
            element: render(
              <GofQuote
                blankDesign={blankDesign}
                saveDesign={handleGofSaveDesign}
                setUpGof={handleGofSetUp}
              />
            ),
            path: 'gofQuote'
          },
          {
            element: render(
              <RouteHandlerConnector handlerName="getPriceHandler" />
            ),
            path: 'getPrice'
          },
          {
            element: render(<Price onClose={onClose} />),
            path: 'price'
          },
          {
            element: render(
              <InternationalQuote setNavigateTo={hooks?.setNavigateTo} />
            ),
            path: 'intlQuote'
          },
          {
            path: 'summary',
            children: [
              {
                index: true,
                element: render(<MultiItemSummaryView />)
              },
              {
                element: render(<MultiItemSummaryView quote />),
                path: 'quote'
              },
              {
                element: render(<MultiItemSummaryView buy />),
                path: 'buy'
              }
            ]
          },
          {
            path: 'changeIconColor',
            children: [
              {
                path: ':id',
                children: [
                  {
                    index: true,
                    element: render(<IconSwatchColorChanger />)
                  },
                  {
                    path: ':colorId',
                    children: [
                      {
                        index: true,
                        element: render(<IconSwatchColorChanger />)
                      },
                      {
                        path: ':duplicateIndex',
                        element: render(<IconSwatchColorChanger />)
                      }
                    ]
                  }
                ]
              }
            ]
          },
          {
            path: 'contentCheck',
            element: render(<ContentCheckCard />)
          },
          {
            element: render(<DesignChecker />),
            path: 'designCheck'
          },
          {
            element: render(<DesignSpinner />),
            path: 'loadingDesignChecker'
          },
          {
            element: render(<MoneySavingTipsView />),
            path: 'tips'
          },
          {
            path: 'inventory',
            element: render(<InventoryIntro />)
          },
          {
            path: 'quote',
            element: render(<Quote />)
          },
          {
            path: ':mode',
            element: render(<Quote />)
          }
        ]
      },
      notFoundRoute
    ],
    location
  );

  return (
    <div
      className={`next-tools-routes ${transitionStage}`}
      onAnimationEnd={() => {
        if (transitionStage === 'slideOut') {
          setTransistionStage('slideIn');
          setDisplayLocation(location);
        }
      }}>
      <div className="next-tools-routes__content">{routes}</div>
    </div>
  );
};
NextToolsRoutes.propTypes = propTypes;

export default NextToolsRoutes;
